import type { ReportDocument, ReportSignatures as ReportSignaturesData } from "../../services/report";
import { approvalLabel, EMPTY_VALUE, formatUtc } from "./reportFormat";
import { ReportWarningNotes } from "./ReportWarningNotes";

// Section 9 of the sheet, traceability and signatures (§20; A31 (d), D5, D6 and D7): engine
// version, the run's stored content hash and the report checksum - both exactly as received,
// the browser never recomputes a checksum - the signature line of D7, prepared by / at,
// submitted by / at, and the approval record: for an APPROVED run the approver's name, user id
// (the audit record), the UTC time and the notes; for any other run the DRAFT state only, with
// a rejection's reason and record when there is one. Then the superseded run, a later revision,
// and two blank lines for a hand signature: the OS has no checker role in V1.0, and a typed
// name without a record would not be honest.
//
// The signature line (CTO decision D7, revised) is read from two values the server derives and
// never stores, `approved_by_name` and `self_checked`; the page compares nothing itself and a
// user id is never printed as a person.

type ReportSignaturesProps = {
  document: ReportDocument;
};

const PREPARER_UNKNOWN = "preparer not recorded";
const APPROVER_UNKNOWN = "approver not on record";

/** The D7 sentence: who prepared, who approved, and whether that was an independent check. */
export function signatureLine(signatures: ReportSignaturesData, approved: boolean): string {
  const preparer = signatures.prepared_by ?? PREPARER_UNKNOWN;
  if (!approved) {
    return `Prepared by ${preparer}`;
  }
  const approver = signatures.approved_by_name ?? APPROVER_UNKNOWN;
  if (signatures.self_checked === true) {
    return `Prepared and approved by ${preparer} - Self-checked: not independently verified`;
  }
  if (signatures.self_checked === false) {
    return `Prepared by ${preparer} - Approved by ${approver}`;
  }
  return `Prepared by ${preparer} - Approved by ${approver} - Independence not recorded`;
}

function person(name: string | null): string {
  return name ?? EMPTY_VALUE;
}

function time(value: string | null): string {
  return value === null ? EMPTY_VALUE : formatUtc(value);
}

export function ReportSignatures({ document }: ReportSignaturesProps) {
  const { signatures, status, selection } = document;
  const approved = status.approval_status === "APPROVED";
  const rejected = status.approval_status === "REJECTED";
  const approvalState = rejected
    ? `${approvalLabel(status.approval_status)} (${signatures.rejection_reason ?? EMPTY_VALUE})`
    : approvalLabel(status.approval_status);

  return (
    <section aria-label="Traceability and signatures" data-report-section="signatures">
      <h3>Traceability and signatures</h3>
      <p
        data-report-field="signature-line"
        data-report-self-checked={
          signatures.self_checked === null ? "unknown" : String(signatures.self_checked)
        }
      >
        <strong>{signatureLine(signatures, approved)}</strong>
      </p>
      <dl>
        <dt>Engine version</dt>
        <dd data-report-field="engine-version">{document.engine_version}</dd>
        <dt>Run content hash (SHA-256)</dt>
        <dd data-report-field="content-hash">
          <code>{document.content_hash}</code>
        </dd>
        <dt>Report checksum (SHA-256 of the stored evidence)</dt>
        <dd data-report-field="report-checksum">
          <code>{document.report_checksum}</code>
        </dd>
        <dt>Prepared by</dt>
        <dd data-report-field="prepared-by">{person(signatures.prepared_by)}</dd>
        <dt>Prepared at</dt>
        <dd data-report-field="prepared-at">{formatUtc(signatures.prepared_at)}</dd>
        <dt>Submitted</dt>
        <dd data-report-field="submitted">
          {signatures.submitted_at === null
            ? "Not submitted for review"
            : `${time(signatures.submitted_at)} by ${person(signatures.submitted_by)}`}
        </dd>
        <dt>Approval</dt>
        <dd data-report-field="approval-status" data-report-approval={status.approval_status}>
          {approved ? approvalState : `DRAFT — ${approvalState}`}
        </dd>
        {approved ? (
          <>
            <dt>Approved by</dt>
            <dd data-report-field="approved-by-name">{person(signatures.approved_by_name)}</dd>
            <dt>Approver user id (audit record)</dt>
            <dd data-report-field="approved-by">
              <code>{person(signatures.approved_by)}</code>
            </dd>
            <dt>Approved at</dt>
            <dd data-report-field="approved-at">{time(signatures.approved_at)}</dd>
            <dt>Approval notes</dt>
            <dd data-report-field="approval-notes">{signatures.approval_notes ?? EMPTY_VALUE}</dd>
          </>
        ) : null}
        {rejected ? (
          <>
            <dt>Rejected by</dt>
            <dd data-report-field="rejected-by">{person(signatures.rejected_by)}</dd>
            <dt>Rejected at</dt>
            <dd data-report-field="rejected-at">{time(signatures.rejected_at)}</dd>
          </>
        ) : null}
        <dt>Supersedes run</dt>
        <dd data-report-field="supersedes">{signatures.supersedes_run_id ?? EMPTY_VALUE}</dd>
        {selection.later_revision_exists ? (
          <>
            <dt>Later revision</dt>
            <dd data-report-field="superseded-by">
              revision {selection.latest_revision_number} exists,{" "}
              {selection.later_revision_approved ? "approved" : "not approved"}
            </dd>
          </>
        ) : null}
      </dl>
      <ReportWarningNotes document={document} section="signatures" />
      <dl data-report-hand-signatures>
        <div data-report-signature="checked-by">
          <dt>Checked by</dt>
          <dd>
            <span aria-hidden="true">______________________</span> (name, date)
          </dd>
        </div>
        <div data-report-signature="reviewed-by">
          <dt>Reviewed by</dt>
          <dd>
            <span aria-hidden="true">______________________</span> (name, date)
          </dd>
        </div>
      </dl>
    </section>
  );
}

export default ReportSignatures;
